import CodeInput from '@/components/TwoFactor/CodeInput';
import MfaErrorAlert from '@/components/TwoFactor/MfaErrorAlert';
import SendCodeButton from '@/components/TwoFactor/SendCodeButton';
import { useSendMfaEmailCode } from '@/http/queries/useSendMfaEmailCode';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import LoadingButton from '@components/Button/LoadingButton';
import PasswordInput from '@components/PasswordInput';
import { Button, Input, Link, Typography } from '@eye-able/ui';
import type { MfaMethodType } from '@eye-able/types';
import { useId, useState, type FormEvent } from 'react';

interface MfaProofFormProps {
  /** The user's active methods; the app is asked first, mail on request. */
  methods: MfaMethodType[];
  /** Where a mailed code goes, named once one went out. */
  email: string;
  /** Off for a user without a method yet: the password is all they can give. */
  askCode?: boolean;
  submitLabel: string;
  onSubmit: (proof: MfaProof) => Promise<unknown>;
  onCancel: () => void;
}

export interface MfaProof {
  password: string;
  /** Absent when no method is active yet. */
  code?: string;
}

type CodeSource = MfaMethodType | 'backup';

const codeLabels: Record<CodeSource, string> = {
  totp: 'mfaCodeFromApp',
  email: 'mfaCodeFromMail',
  backup: 'mfaBackupCode',
};

/**
 * Changing the second factor costs password and a current code, so a stolen
 * session alone cannot weaken the account. Shared by add, remove and
 * regenerate. A mailed code is only mentioned once the user asked for it.
 */
function MfaProofForm({
  methods,
  email,
  askCode = true,
  submitLabel,
  onSubmit,
  onCancel,
}: MfaProofFormProps) {
  const { t } = useInlineTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const primary: MfaMethodType = methods.includes('totp') ? 'totp' : 'email';
  const [source, setSource] = useState<CodeSource>(primary);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [passwordWrong, setPasswordWrong] = useState(false);
  const send = useSendMfaEmailCode();
  const codeId = useId();
  const errorId = useId();
  const passwordErrorId = useId();

  const sent = !!send.data;
  // The mail field waits for its code; the app and backup fields are always there.
  const codeFieldShown = source !== 'email' || sent;
  const complete =
    password.length > 0 &&
    (!askCode || (codeFieldShown && code.trim().length > 0));

  const switchTo = (next: CodeSource) => {
    setSource(next);
    setCode('');
    setError(null);
    if (next === 'email' && !sent) send.mutate({});
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!complete || pending) return;
    setPending(true);
    setError(null);
    setPasswordWrong(false);
    try {
      await onSubmit(askCode ? { password, code: code.trim() } : { password });
    } catch (failure: unknown) {
      const { status } = (failure ?? {}) as { status?: number };
      if (status === 400) {
        setPasswordWrong(true);
      } else {
        setError(failure);
        setCode('');
      }
    } finally {
      setPending(false);
    }
  };

  const otherMethod = methods.find((type) => type !== source);

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className='eye:flex eye:flex-col eye:gap-16'
    >
      <PasswordInput
        label={t('password')}
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
          setPasswordWrong(false);
        }}
        autoComplete='current-password'
        aria-invalid={passwordWrong || undefined}
        aria-describedby={passwordWrong ? passwordErrorId : undefined}
        required
        autoFocus
      />
      {passwordWrong ? (
        <Typography
          id={passwordErrorId}
          variant='body-s'
          className='eye:text-red-100'
          role='alert'
        >
          <T k='mfaWrongPassword' />
        </Typography>
      ) : null}

      {askCode && source === 'email' ? (
        <SendCodeButton
          sent={sent}
          resendAt={send.data?.resendAt ?? null}
          onSend={() => send.mutate({})}
          disabled={send.isPending}
          email={email}
          prominent
        />
      ) : null}

      {askCode && codeFieldShown ? (
        <div className='eye:flex eye:flex-col eye:gap-3xs'>
          {source === 'backup' ? (
            <Input
              id={codeId}
              label={t('mfaBackupCode')}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setError(null);
              }}
              autoComplete='off'
              autoCapitalize='characters'
              spellCheck={false}
              aria-invalid={!!error || undefined}
              aria-describedby={error ? errorId : undefined}
              disabled={pending}
            />
          ) : (
            <>
              <Typography
                tag='label'
                variant='body-m-highlight'
                htmlFor={codeId}
              >
                <T k={codeLabels[source]} />
              </Typography>
              <CodeInput
                id={codeId}
                value={code}
                onChange={(value) => {
                  setCode(value);
                  setError(null);
                }}
                describedBy={error ? errorId : undefined}
                invalid={!!error}
                disabled={pending}
              />
            </>
          )}
        </div>
      ) : null}

      {askCode ? (
        <ul className='eye:flex eye:flex-wrap eye:gap-16'>
          {source === 'backup' ? (
            <li>
              <Link size='small' asChild>
                <button
                  type='button'
                  className='eye:cursor-pointer'
                  onClick={() => switchTo(primary)}
                >
                  {t(`mfaUseMethodInstead_${primary}`)}
                </button>
              </Link>
            </li>
          ) : (
            <>
              {otherMethod ? (
                <li>
                  <Link size='small' asChild>
                    <button
                      type='button'
                      className='eye:cursor-pointer'
                      onClick={() => switchTo(otherMethod)}
                    >
                      {t(`mfaUseMethodInstead_${otherMethod}`)}
                    </button>
                  </Link>
                </li>
              ) : null}
              <li>
                <Link size='small' asChild>
                  <button
                    type='button'
                    className='eye:cursor-pointer'
                    onClick={() => switchTo('backup')}
                  >
                    {t('mfaUseBackupCode')}
                  </button>
                </Link>
              </li>
            </>
          )}
        </ul>
      ) : null}

      <MfaErrorAlert id={errorId} error={error ?? send.error} />
      <div className='eye:flex eye:justify-end eye:gap-2xs'>
        <Button
          type='button'
          variant='outline'
          onClick={onCancel}
          disabled={pending}
        >
          {t('cancel')}
        </Button>
        <LoadingButton
          type='submit'
          variant='yellow'
          loading={pending}
          disabled={!complete}
        >
          {submitLabel}
        </LoadingButton>
      </div>
    </form>
  );
}

export default MfaProofForm;
